// csv.js — CSV-Parsing für PVS-Exporte (DS-Win, Z1, charly …).
//
// Besonderheiten deutscher Praxis-Exporte, die hier abgedeckt werden:
//  - Trennzeichen meist Semikolon (Excel-DE), manchmal Komma oder Tab
//  - Encoding oft Windows-1252/ISO-8859-1 statt UTF-8 (Umlaute!)
//  - Felder teils in Anführungszeichen, mit Semikolons im Wert

// Datei einlesen mit Encoding-Erkennung: erst UTF-8 versuchen; tauchen
// Ersatzzeichen (�) auf, war es ein Windows-Export → als windows-1252 lesen.
export function readFileSmart(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const buf = reader.result;
      let text = new TextDecoder("utf-8").decode(buf);
      if (text.includes("�")) {
        text = new TextDecoder("windows-1252").decode(buf);
      }
      if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1); // BOM
      resolve(text);
    };
    reader.readAsArrayBuffer(file);
  });
}

// Trennzeichen raten: das Zeichen, das in der Kopfzeile am häufigsten
// außerhalb von Anführungszeichen vorkommt.
export function detectDelimiter(text) {
  const firstLine = text.split(/\r?\n/, 1)[0] || "";
  let best = ";", bestCount = -1;
  for (const d of [";", ",", "\t", "|"]) {
    let count = 0, inQuotes = false;
    for (const ch of firstLine) {
      if (ch === '"') inQuotes = !inQuotes;
      else if (ch === d && !inQuotes) count++;
    }
    if (count > bestCount) { best = d; bestCount = count; }
  }
  return best;
}

// Einfacher, robuster CSV-Parser (Anführungszeichen, "" als Escape, CRLF).
export function parseCsv(text, delimiter) {
  const d = delimiter || detectDelimiter(text);
  const rows = [];
  let row = [], field = "", inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === d) {
      row.push(field); field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field); field = "";
      if (row.length > 1 || row[0] !== "") rows.push(row);
      row = [];
    } else field += ch;
  }
  if (field !== "" || row.length) { row.push(field); if (row.length > 1 || row[0] !== "") rows.push(row); }
  if (!rows.length) return { headers: [], rows: [] };
  const headers = rows[0].map(h => h.trim());
  return { headers, rows: rows.slice(1).map(r => r.map(c => c.trim())) };
}

// ── Spalten-Mapping automatisch vorschlagen ───────────────────────────────
// Synonyme, wie sie in PVS-Exporten typischerweise heißen (kleingeschrieben,
// ohne Sonderzeichen verglichen).
const SYNONYMS = {
  vorname:   ["vorname", "firstname", "first name", "pat vorname"],
  nachname:  ["nachname", "name", "lastname", "familienname", "pat name", "pat nachname"],
  dob:       ["geburtsdatum", "geb datum", "gebdatum", "geb", "geburtstag", "dob", "geboren"],
  kv:        ["krankenkasse", "kasse", "kv", "versicherung", "kostentraeger", "kostentraegername"],
  treat:     ["behandler", "zahnarzt", "arzt", "stammbehandler", "beh"],
  last:      ["letzter termin", "letzter besuch", "letzte behandlung", "letzter kontakt"],
  since:     ["patient seit", "aufnahmedatum", "aufnahme", "erstaufnahme", "seit"],
  status:    ["status", "patientenstatus"],
  lastPzr:   ["letzte pzr", "pzr datum", "letzte prophylaxe", "prophylaxe datum", "letzte zahnreinigung"],
  monat:     ["monat", "periode", "zeitraum", "abrechnungsmonat", "jahr monat"],
  umsatz:    ["umsatz", "honorar", "gesamtumsatz", "betrag", "honorarumsatz", "gesamthonorar"],
  neupatienten: ["neupatienten", "neue patienten", "neuaufnahmen"],
  privatanteil: ["privatanteil", "privat", "privatumsatz", "goz anteil", "goz"],
  // Umsatzstatistik je Behandler (DS-Win & Co. exportieren Behandler als
  // Spalte oder Kürzel — je nach Statistik-Typ)
  behandler: ["behandler", "zahnarzt", "arzt", "behandlerkuerzel", "behandlerkürzel", "kuerzel", "kürzel", "behandlername", "bhd"],
  email: ["email", "e mail", "e mail adresse", "emailadresse", "mail", "mailadresse"],
  // Leistungsstatistik (BEMA/GOZ-Ziffern). "faktor" fehlt bewusst: in der GOZ
  // ist das der Steigerungsfaktor, nicht die Anzahl.
  ziffer:      ["ziffer", "gebuehrenziffer", "gebührenziffer", "leistungsziffer", "goz ziffer", "bema ziffer", "abrechnungsnummer", "leistungsnummer", "position"],
  anzahl:      ["anzahl", "menge", "haeufigkeit", "häufigkeit", "abrechnungen", "frequenz", "stueckzahl", "stückzahl"],
  betrag:      ["honorar", "betrag", "summe", "gesamtbetrag", "honorarsumme"],
  katalog:     ["katalog", "gebuehrenordnung", "gebührenordnung", "leistungsart", "abrechnungsart"],
  bezeichnung: ["bezeichnung", "leistungsbezeichnung", "beschreibung", "leistungstext", "leistung"],
};

const norm = (s) => s.toLowerCase().replace(/[^a-zäöüß ]/g, " ").replace(/\s+/g, " ").trim();

export function guessMapping(headers, targetFields) {
  const mapping = {};
  targetFields.forEach(f => {
    const syns = SYNONYMS[f] || [f];
    let hit = -1;
    // exakter Treffer zuerst, dann enthält-Vergleich
    hit = headers.findIndex(h => syns.includes(norm(h)));
    if (hit < 0) hit = headers.findIndex(h => syns.some(s => norm(h).includes(s)));
    if (hit >= 0) mapping[f] = hit;
  });
  return mapping;
}
